import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { assessmentService } from '../../api';
import QuestionCard from '../../components/Assessment/QuestionCard';
import toast from 'react-hot-toast';
import { Clock, ChevronLeft, ChevronRight, Send, AlertCircle } from 'lucide-react';

function AssessmentTake() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [assessment, setAssessment] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [submission, setSubmission] = useState(null);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState({});
    const [loading, setLoading] = useState(true);
    const [timeLeft, setTimeLeft] = useState(null);
    const timerRef = useRef(null);

    useEffect(() => {
        const initAssessment = async () => {
            try {
                // 1. Get assessment details
                const detailRes = await assessmentService.getAssessmentDetail(id);
                setAssessment(detailRes.data);

                // 2. Try to resume or start new submission
                let subRes;
                try {
                    subRes = await assessmentService.getCurrentSubmission(id);
                } catch (err) {
                    subRes = await assessmentService.startSubmission(id);
                }
                setSubmission(subRes.data);

                // 3. Fetch questions
                const questionsRes = await assessmentService.getQuestions(id);
                const sortedQuestions = (questionsRes.data.results || questionsRes.data).sort((a, b) => a.order - b.order);
                setQuestions(sortedQuestions);

                // 4. Set timer
                const startTime = new Date(subRes.data.started_at);
                const limitMinutes = subRes.data.time_limit_minutes || detailRes.data.time_limit_minutes;
                const endTime = new Date(startTime.getTime() + limitMinutes * 60000);
                const now = new Date();
                const diff = Math.floor((endTime - now) / 1000);

                if (diff <= 0) {
                    toast.error('Time limit exceeded!');
                    handleSubmit();
                    return;
                }
                setTimeLeft(diff);

            } catch (err) {
                console.error('Error starting assessment:', err);
                toast.error(err.response?.data?.error || 'Failed to start assessment.');
                navigate('/assessments');
            } finally {
                setLoading(false);
            }
        };

        initAssessment();
    }, [id, navigate]);

    useEffect(() => {
        if (timeLeft !== null && timeLeft > 0) {
            timerRef.current = setInterval(() => {
                setTimeLeft(prev => {
                    if (prev <= 1) {
                        clearInterval(timerRef.current);
                        handleSubmit();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => clearInterval(timerRef.current);
    }, [timeLeft]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const handleAnswerChange = (questionId, value) => {
        setAnswers(prev => ({
            ...prev,
            [questionId]: value
        }));
    };

    const handleSubmit = async () => {
        if (Object.keys(answers).length < questions.length) {
            if (!window.confirm('You haven\'t answered all questions. Submit anyway?')) {
                return;
            }
        }

        clearInterval(timerRef.current);
        const loadingToast = toast.loading('Submitting assessment...');
        try {
            const res = await assessmentService.submitAssessment(id, answers);
            toast.success('Assessment submitted!', { id: loadingToast });
            const submissionId = res.data?.id || res.data?.submission_id;
            if (submissionId) {
                navigate(`/assessments/${submissionId}/result`);
            } else {
                navigate('/assessments');
            }
        } catch (err) {
            toast.error('Failed to submit assessment.', { id: loadingToast });
        }
    };

    if (loading) return <DashboardLayout showTopBar={false}><div className="p-xl text-center">Preparing questions...</div></DashboardLayout>;
    if (!assessment || questions.length === 0) return <DashboardLayout showTopBar={false}><div className="p-xl text-center">No questions available.</div></DashboardLayout>;

    const currentQuestion = questions[currentQuestionIndex];

    return (
        <DashboardLayout showTopBar={false}>
        <div className="assessment-take-page flex flex-col">
            {/* Header / Nav */}
            <header className="bg-theme-card border-b px-xl py-md sticky top-0 z-10 flex justify-between items-center shadow-sm">
                <div className="flex items-center gap-md">
                    <button onClick={() => navigate('/assessments')} className="text-muted hover:text-dark">
                        <ChevronLeft size={24} />
                    </button>
                    <div>
                        <h1 className="font-bold text-lg leading-tight">{assessment.title}</h1>
                        <p className="text-xs text-muted">Attempt #{submission?.attempt_number || 1}</p>
                    </div>
                </div>

                <div className={`timer-box flex items-center gap-sm px-lg py-sm rounded-pill font-bold ${timeLeft < 300 ? 'bg-red-50 text-red-600' : 'bg-teal-50 text-teal-700'
                    }`}>
                    <Clock size={20} />
                    <span>{formatTime(timeLeft)}</span>
                </div>

                <button className="btn btn-primary" onClick={handleSubmit}>
                    Finish Assessment
                </button>
            </header>

            <main className="container flex-1 py-xl">
                <div className="max-w-3xl mx-auto">
                    {/* Question Navigator info */}
                    <div className="flex justify-between items-center mb-lg">
                        <span className="font-bold text-muted">QUESTION {currentQuestionIndex + 1} OF {questions.length}</span>
                        <div className="flex gap-xs">
                            {questions.map((_, idx) => (
                                <div
                                    key={idx}
                                    className={`w-8 h-1.5 rounded-pill transition-all ${idx === currentQuestionIndex ? 'bg-primary' : answers[questions[idx].id] ? 'bg-success bg-opacity-40' : 'bg-gray-200'
                                        }`}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Question Content */}
                    <div className="bg-theme-card p-2xl rounded-3xl shadow-sm border border-theme-border min-h-[400px]">
                        <QuestionCard
                            key={currentQuestion.id}
                            question={currentQuestion}
                            currentAnswer={answers[currentQuestion.id]}
                            onAnswerChange={(val) => handleAnswerChange(currentQuestion.id, val)}
                        />
                    </div>

                    {/* Controls */}
                    <div className="flex justify-between mt-xl">
                        <button
                            className="btn btn-outline gap-sm"
                            disabled={currentQuestionIndex === 0}
                            onClick={() => setCurrentQuestionIndex(i => i - 1)}
                        >
                            <ChevronLeft size={20} /> Previous
                        </button>

                        {currentQuestionIndex < questions.length - 1 ? (
                            <button
                                className="btn btn-primary gap-sm"
                                onClick={() => setCurrentQuestionIndex(i => i + 1)}
                            >
                                Next Question <ChevronRight size={20} />
                            </button>
                        ) : (
                            <button type="button" className="btn btn-success gap-sm" onClick={handleSubmit}>
                                <Send size={20} /> Submit Assessment
                            </button>
                        )}
                    </div>
                </div>
            </main>

            {/* Warning if time low */}
            {timeLeft < 60 && (
                <div className="fixed bottom-lg right-lg animate-bounce">
                    <div className="bg-red-600 text-white p-md rounded-2xl shadow-xl flex items-center gap-md">
                        <AlertCircle />
                        <span>Less than 1 minute remaining!</span>
                    </div>
                </div>
            )}
        </div>
        </DashboardLayout>
    );
}

export default AssessmentTake;
